import { GeneralParameters } from "@imi-ernd-be/hyselect-engine";
import { TechnicalSettings } from "@imi/api-client/index.js";
import { setIfNotNil } from "@src/shared/helpers/setIfNotNil";
import { assert } from "radashi";
import { engine } from "../..";
import { getBooleanDefinition } from "../../helpers/fieldDefinitions/getBooleanDefinition";
import { getNumberDefinition } from "../../helpers/fieldDefinitions/getNumberDefinition";
import { trackLiveObjects } from "../../helpers/trackLiveObjects";

export type TechnicalSettingsDefinitions = ReturnType<
  typeof technicalSettingsRepository.getDefinitions
>;

export const technicalSettingsRepository = trackLiveObjects(
  "technicalSettingsRepository",
  {
    getDefinitions() {
      const technicalParameters = new engine.TechnicalParameters();
      const generalParameters = technicalParameters.getGeneralParams();
      assert(
        generalParameters,
        "General parameters are required to get definitions",
      );

      const {
        balancingValve,
        deviceSizes,
        dpControllers,
        fixedOrifices,
        pibcvParams,
        pipes,
        pressure,
        radiatorValves,
        smartControlValves,
        stdAndAdjControlValve,
        waterQuality,
      } = technicalSettingsRepository.getParametersGroups(generalParameters);

      const definitions = {
        balancingValves: {
          maxPressureDrop: getNumberDefinition(
            balancingValve.getMaxPressureDrop(),
          ),
          minPressureDrop: getNumberDefinition(
            balancingValve.getMinPressureDrop(),
          ),
        },
        deviceSizes: {
          sizeAbove: getNumberDefinition(deviceSizes.getSizeAbove()),
          sizeBelow: getNumberDefinition(deviceSizes.getSizeBelow()),
        },
        dpControllers: {
          hideNonSatisfied: getBooleanDefinition(
            dpControllers.getHideNonSatisfied(),
          ),
          minDp: getNumberDefinition(dpControllers.getMinDp()),
          suggestSameSize: getBooleanDefinition(
            dpControllers.getSuggestSameSize(),
          ),
        },
        fixedOrifices: {
          maxSignal: getNumberDefinition(fixedOrifices.getMaxSignal()),
          minSignal: getNumberDefinition(fixedOrifices.getMinSignal()),
        },
        pibcvParams: {
          extraDp: getNumberDefinition(pibcvParams.getExtraDp()),
          hideNonSatisfied: getBooleanDefinition(
            pibcvParams.getHideNonSatisfied(),
          ),
          minFullyOpenDp: getNumberDefinition(pibcvParams.getMinFullyOpenDp()),
        },
        pipes: {
          maxPressureDrop: getNumberDefinition(pipes.getMaxPressureDrop()),
          maxWaterVelocity: getNumberDefinition(pipes.getMaxWaterVelocity()),
          minWaterVelocity: getNumberDefinition(pipes.getMinWaterVelocity()),
          targetPressureDrop: getNumberDefinition(
            pipes.getTargetPressureDrop(),
          ),
          targetWaterVelocity: getBooleanDefinition(
            pipes.getTargetWaterVelocity(),
          ),
          targetWaterVelocityValue: getNumberDefinition(
            pipes.getTargetWaterVelocityValue(),
          ),
        },
        pressure: {
          maxAdditionalVesselsInParallel: getNumberDefinition(
            pressure.getMaxAdditionalVesselsInParallel(),
          ),
          maxDerogation: getNumberDefinition(pressure.getMaxDerogation()),
          maxVentoInParallel: getNumberDefinition(
            pressure.getMaxVentoInParallel(),
          ),
          simplifyCalculation: getBooleanDefinition(
            pressure.getSimplifyCalculation(),
          ),
          splitVessels: getBooleanDefinition(pressure.getSplitVessels()),
          splitVesselsValue: getNumberDefinition(
            pressure.getSplitVesselsValue(),
          ),
        },
        radiatorValves: {
          defaultDifferentialPressure: getNumberDefinition(
            radiatorValves.getDefaultDifferentialPressure(),
          ),
          defaultRoomTemperature: getNumberDefinition(
            radiatorValves.getDefaultRoomTemperature(),
          ),
          defaultSupplyTemperature: getNumberDefinition(
            radiatorValves.getDefaultSupplyTemperature(),
          ),
        },
        smartControlValves: {
          maxDpMin: getNumberDefinition(smartControlValves.getMaxDpMin()),
        },
        stdAndAdjControlValves: {
          constantFlowMin: getNumberDefinition(
            stdAndAdjControlValve.getConstantFlowMinAuthority(),
          ),
          maxProportionalDp: getNumberDefinition(
            stdAndAdjControlValve.getMaxProportionalDp(),
          ),
          minOnOffDp: getNumberDefinition(
            stdAndAdjControlValve.getMinOnOffDp(),
          ),
          minProportionalDp: getNumberDefinition(
            stdAndAdjControlValve.getMinProportionalDp(),
          ),
          selectKvFromReynard: getBooleanDefinition(
            stdAndAdjControlValve.getSelectKvFromReynard(),
          ),
          usePBand: getBooleanDefinition(stdAndAdjControlValve.getUsePBand()),
          variableFlowMin: getNumberDefinition(
            stdAndAdjControlValve.getVariableFlowMinAuthority(),
          ),
        },
        waterQuality: {
          defaultHardness: getNumberDefinition(
            waterQuality.getDefaultHardness(),
          ),
        },
      };
      technicalParameters.delete();
      return definitions;
    },
    getParametersGroups(generalParameters: GeneralParameters) {
      const balancingValve = generalParameters.getBalancingValveParams();
      const deviceSizes = generalParameters.getDeviceSizesParams();
      const dpControllers = generalParameters.getDpControllersParams();
      const fixedOrifices = generalParameters.getFixedOrificesParams();
      const pibcvParams = generalParameters.getPIBCVParams();
      const pipes = generalParameters.getPipesParams();
      const pressure = generalParameters.getPressureParams();
      const radiatorValves = generalParameters.getRadiatorValvesParams();
      const smartControlValves =
        generalParameters.getSmartControlValvesParams();
      const stdAndAdjControlValve =
        generalParameters.getStdAndAdjControlValvesParams();
      const waterQuality = generalParameters.getWaterQualityParams();

      assert(balancingValve, "generalParameters.balancingValve is null");
      assert(deviceSizes, "generalParameters.deviceSizes is null");
      assert(dpControllers, "generalParameters.dpControllers is null");
      assert(fixedOrifices, "generalParameters.fixedOrifices is null");
      assert(pibcvParams, "generalParameters.pibcvParams is null");
      assert(pipes, "generalParameters.pipes is null");
      assert(pressure, "generalParameters.pressure is null");
      assert(radiatorValves, "generalParameters.radiatorValves is null");
      assert(
        smartControlValves,
        "generalParameters.smartControlValves is null",
      );
      assert(
        stdAndAdjControlValve,
        "generalParameters.stdAndAdjControlValve is null",
      );
      assert(waterQuality, "generalParameters.waterQuality is null");

      return {
        balancingValve,
        deviceSizes,
        dpControllers,
        fixedOrifices,
        pibcvParams,
        pipes,
        pressure,
        radiatorValves,
        smartControlValves,
        stdAndAdjControlValve,
        waterQuality,
      };
    },
    init(
      generalParameters: GeneralParameters,
      technicalSettings: Partial<TechnicalSettings> | null,
    ) {
      const {
        balancingValve,
        deviceSizes,
        dpControllers,
        fixedOrifices,
        pibcvParams,
        pipes,
        pressure,
        radiatorValves,
        smartControlValves,
        stdAndAdjControlValve,
        waterQuality,
      } = technicalSettingsRepository.getParametersGroups(generalParameters);

      // Balancing Valve
      setIfNotNil(
        balancingValve.setMaxPressureDrop.bind(balancingValve),
        technicalSettings?.balancingValves?.maxPressureDrop,
      );
      setIfNotNil(
        balancingValve.setMinPressureDrop.bind(balancingValve),
        technicalSettings?.balancingValves?.minPressureDrop,
      );

      // Device Sizes
      setIfNotNil(
        deviceSizes.setValveSizeSelectionChoice.bind(deviceSizes),
        "1",
      );
      setIfNotNil(
        deviceSizes.setSizeAbove.bind(deviceSizes),
        technicalSettings?.deviceSizes?.sizeAbove,
      );
      setIfNotNil(
        deviceSizes.setSizeBelow.bind(deviceSizes),
        technicalSettings?.deviceSizes?.sizeBelow,
      );

      // DP Controllers
      setIfNotNil(
        dpControllers.setHideNonSatisfied.bind(dpControllers),
        technicalSettings?.dpControllers?.hideNonSatisfied,
      );
      setIfNotNil(
        dpControllers.setMinDp.bind(dpControllers),
        technicalSettings?.dpControllers?.minDp,
      );
      setIfNotNil(
        dpControllers.setSuggestSameSize.bind(dpControllers),
        technicalSettings?.dpControllers?.suggestSameSize,
      );

      // Fixed Orifices
      setIfNotNil(
        fixedOrifices.setMaxSignal.bind(fixedOrifices),
        technicalSettings?.fixedOrifices?.maxSignal,
      );
      setIfNotNil(
        fixedOrifices.setMinSignal.bind(fixedOrifices),
        technicalSettings?.fixedOrifices?.minSignal,
      );

      // pibcv Params
      setIfNotNil(
        pibcvParams.setExtraDp.bind(pibcvParams),
        technicalSettings?.pibcvParams?.extraDp,
      );
      setIfNotNil(
        pibcvParams.setHideNonSatisfied.bind(pibcvParams),
        technicalSettings?.pibcvParams?.hideNonSatisfied,
      );
      setIfNotNil(
        pibcvParams.setMinFullyOpenDp.bind(pibcvParams),
        technicalSettings?.pibcvParams?.minFullyOpenDp,
      );

      // Pipes
      setIfNotNil(
        pipes.setMaxPressureDrop.bind(pipes),
        technicalSettings?.pipes?.maxPressureDrop,
      );
      setIfNotNil(
        pipes.setMaxWaterVelocity.bind(pipes),
        technicalSettings?.pipes?.maxWaterVelocity,
      );
      setIfNotNil(
        pipes.setMinWaterVelocity.bind(pipes),
        technicalSettings?.pipes?.minWaterVelocity,
      );
      setIfNotNil(
        pipes.setTargetPressureDrop.bind(pipes),
        technicalSettings?.pipes?.targetPressureDrop,
      );
      setIfNotNil(
        pipes.setTargetWaterVelocity.bind(pipes),
        technicalSettings?.pipes?.targetWaterVelocity,
      );
      setIfNotNil(
        pipes.setTargetWaterVelocityValue.bind(pipes),
        technicalSettings?.pipes?.targetWaterVelocityValue,
      );

      // Pressure
      setIfNotNil(
        pressure.setMaxAdditionalVesselsInParallel.bind(pressure),
        technicalSettings?.pressure?.maxAdditionalVesselsInParallel,
      );
      setIfNotNil(
        pressure.setMaxDerogation.bind(pressure),
        technicalSettings?.pressure?.maxDerogation,
      );
      setIfNotNil(
        pressure.setMaxVentoInParallel.bind(pressure),
        technicalSettings?.pressure?.maxVentoInParallel,
      );
      setIfNotNil(
        pressure.setSimplifyCalculation.bind(pressure),
        technicalSettings?.pressure?.simplifyCalculation,
      );
      setIfNotNil(
        pressure.setSplitVessels.bind(pressure),
        technicalSettings?.pressure?.splitVessels,
      );
      setIfNotNil(
        pressure.setSplitVesselsValue.bind(pressure),
        technicalSettings?.pressure?.splitVesselsValue,
      );

      // Radiator Valves
      setIfNotNil(
        radiatorValves.setDefaultDifferentialPressure.bind(radiatorValves),
        technicalSettings?.radiatorValves?.defaultDifferentialPressure,
      );
      setIfNotNil(
        radiatorValves.setDefaultRoomTemperature.bind(radiatorValves),
        technicalSettings?.radiatorValves?.defaultRoomTemperature,
      );
      setIfNotNil(
        radiatorValves.setDefaultSupplyTemperature.bind(radiatorValves),
        technicalSettings?.radiatorValves?.defaultSupplyTemperature,
      );

      // Smart Control Valves
      setIfNotNil(
        smartControlValves.setMaxDpMin.bind(smartControlValves),
        technicalSettings?.smartControlValves?.maxDpMin,
      );

      // Std And Adj Control Valve
      setIfNotNil(
        stdAndAdjControlValve.setConstantFlowMinAuthority.bind(
          stdAndAdjControlValve,
        ),
        technicalSettings?.stdAndAdjControlValves?.constantFlowMin,
      );
      setIfNotNil(
        stdAndAdjControlValve.setMaxProportionalDp.bind(stdAndAdjControlValve),
        technicalSettings?.stdAndAdjControlValves?.maxProportionalDp,
      );
      setIfNotNil(
        stdAndAdjControlValve.setMinOnOffDp.bind(stdAndAdjControlValve),
        technicalSettings?.stdAndAdjControlValves?.minOnOffDp,
      );
      setIfNotNil(
        stdAndAdjControlValve.setMinProportionalDp.bind(stdAndAdjControlValve),
        technicalSettings?.stdAndAdjControlValves?.minProportionalDp,
      );
      setIfNotNil(
        stdAndAdjControlValve.setSelectKvFromReynard.bind(
          stdAndAdjControlValve,
        ),
        technicalSettings?.stdAndAdjControlValves?.selectKvFromReynard,
      );
      setIfNotNil(
        stdAndAdjControlValve.setUsePBand.bind(stdAndAdjControlValve),
        technicalSettings?.stdAndAdjControlValves?.usePBand,
      );
      setIfNotNil(
        stdAndAdjControlValve.setVariableFlowMinAuthority.bind(
          stdAndAdjControlValve,
        ),
        technicalSettings?.stdAndAdjControlValves?.variableFlowMin,
      );

      // Water Quality
      setIfNotNil(
        waterQuality.setDefaultHardness.bind(waterQuality),
        technicalSettings?.waterQuality?.defaultHardness,
      );

      generalParameters.setBalancingValveParams(balancingValve);
      generalParameters.setDeviceSizesParams(deviceSizes);
      generalParameters.setDpControllersParams(dpControllers);
      generalParameters.setFixedOrificesParams(fixedOrifices);
      generalParameters.setPIBCVParams(pibcvParams);
      generalParameters.setPipesParams(pipes);
      generalParameters.setPressureParams(pressure);
      generalParameters.setRadiatorValvesParams(radiatorValves);
      generalParameters.setSmartControlValvesParams(smartControlValves);
      generalParameters.setStdAndAdjControlValvesParams(stdAndAdjControlValve);
      generalParameters.setWaterQualityParams(waterQuality);
    },
  },
);
